import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  createOfferte,
  type CreateOfferteItemInput,
  type OfferteChannel,
} from '../../lib/offerte-create';
import type { KleedVorm } from '../../lib/lead-product';
import { PhoneNumber } from '../phone/PhoneNumber';
import type {
  RepeatOrderPrefill,
  RepeatOrderPrefillKleed,
} from '../../lib/repeat-order-prefill';
import {
  OFFERTE_PRIMARY_PRODUCTS,
  DEFAULT_OFFERTE_PRIMARY_SKU,
  findOffertePrimaryProduct,
} from '../../lib/offerte-products';
import {
  PACKAGE_SKUS,
  PACKAGE_LABELS,
  type PackageSku,
} from '../../lib/offerte-constants';

interface RepeatOrderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  prefill: RepeatOrderPrefill;
  onCreated: (leadId: string, channel: OfferteChannel, workOrderId?: string) => void;
}

interface KleedRow {
  productCode: string;
  vorm: KleedVorm;
  lengte: string;
  breedte: string;
  diameter: string;
  oppervlakte: string;
  aantal: string;
  bijzonderheden: string;
}

function prefillToRow(k: RepeatOrderPrefillKleed): KleedRow {
  return {
    productCode: k.productCode,
    vorm: k.vorm,
    lengte: k.lengteCm != null ? String(k.lengteCm) : '',
    breedte: k.breedteCm != null ? String(k.breedteCm) : '',
    diameter: k.diameterCm != null ? String(k.diameterCm) : '',
    oppervlakte: k.oppervlakteM2 != null ? String(k.oppervlakteM2) : '',
    aantal: String(k.aantal || 1),
    bijzonderheden: k.bijzonderheden || '',
  };
}

function emptyRow(): KleedRow {
  return {
    productCode: DEFAULT_OFFERTE_PRIMARY_SKU,
    vorm: 'rechthoekig',
    lengte: '',
    breedte: '',
    diameter: '',
    oppervlakte: '',
    aantal: '1',
    bijzonderheden: '',
  };
}

function rowToItem(row: KleedRow): CreateOfferteItemInput | null {
  const product = findOffertePrimaryProduct(row.productCode);
  if (product.mode === 'count') {
    const n = Number(row.aantal);
    if (!(n > 0)) return null;
    const item: CreateOfferteItemInput = {
      productCode: row.productCode,
      aantal: n,
    };
    if (row.bijzonderheden.trim()) item.bijzonderheden = row.bijzonderheden.trim();
    return item;
  }
  const item: CreateOfferteItemInput = {
    productCode: row.productCode,
    vorm: row.vorm,
  };
  if (row.vorm === 'rechthoekig') {
    const l = Number(row.lengte);
    const b = Number(row.breedte);
    if (!(l > 0) || !(b > 0)) return null;
    item.lengteCm = l;
    item.breedteCm = b;
  } else if (row.vorm === 'rond') {
    const d = Number(row.diameter);
    if (!(d > 0)) return null;
    item.diameterCm = d;
  } else {
    const m2 = Number(row.oppervlakte);
    if (!(m2 > 0)) return null;
    item.oppervlakteM2 = m2;
  }
  if (row.bijzonderheden.trim()) item.bijzonderheden = row.bijzonderheden.trim();
  return item;
}

function summarizeKleed(row: KleedRow): string {
  const product = findOffertePrimaryProduct(row.productCode);
  if (product.mode === 'count') {
    const n = Number(row.aantal);
    return n > 0 ? `${product.label} × ${n}` : product.label;
  }
  if (row.vorm === 'rechthoekig' && row.lengte && row.breedte) {
    return `${product.label} · ${row.lengte} × ${row.breedte} cm`;
  }
  if (row.vorm === 'rond' && row.diameter) {
    return `${product.label} · Ø ${row.diameter} cm`;
  }
  if (row.vorm === 'overig' && row.oppervlakte) {
    return `${product.label} · ${row.oppervlakte} m²`;
  }
  return product.label;
}

const VORM_OPTIONS: { value: KleedVorm; label: string }[] = [
  { value: 'rechthoekig', label: 'Rechthoekig' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
];

export function RepeatOrderDialog({ isOpen, onClose, prefill, onCreated }: RepeatOrderDialogProps) {
  const [rows, setRows] = useState<KleedRow[]>(() => prefill.kleden.map(prefillToRow));
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(
    () => new Set(prefill.kleden.map((_, i) => i))
  );
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [packageSku, setPackageSku] = useState<PackageSku | ''>(
    () => (prefill.packageSku as PackageSku | null) ?? ''
  );
  const [adresUitgeklapt, setAdresUitgeklapt] = useState(false);
  const [street, setStreet] = useState(prefill.address.street);
  const [postalCode, setPostalCode] = useState(prefill.address.postalCode);
  const [city, setCity] = useState(prefill.address.city);
  const [suffix, setSuffix] = useState(prefill.address.houseNumberSuffix);
  const [submitting, setSubmitting] = useState<OfferteChannel | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Als een ORDER strandt op een ontbrekend telefoonnummer bieden we een bewuste
  // doorzet-optie aan (channel onthouden zodat de her-submit het juiste kanaal gebruikt).
  const [phoneConfirmChannel, setPhoneConfirmChannel] = useState<OfferteChannel | null>(null);
  // Optionele korting op de hele vervolgorder/offerte (percentage als string-invoer).
  const [kortingPercentage, setKortingPercentage] = useState('');

  const hasSelected = useMemo(() => selectedIdx.size > 0, [selectedIdx]);
  const canSubmit = hasSelected && submitting === null;

  // Pakket geldt offerte-breed; alleen relevant als minstens één geselecteerd
  // product pakketten ondersteunt (Vloerkleed/Wederverkoper, niet Kussen/Impregneren).
  const anySelectedSupportsPackages = useMemo(
    () =>
      rows.some(
        (row, idx) => selectedIdx.has(idx) && findOffertePrimaryProduct(row.productCode).hasPackages
      ),
    [rows, selectedIdx]
  );

  if (!isOpen) return null;

  const customerName = [prefill.customer.firstName, prefill.customer.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || '—';

  const toggleSelected = (idx: number) => {
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const updateRow = (idx: number, patch: Partial<KleedRow>) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const handleAddKleed = () => {
    const newIdx = rows.length;
    setRows((prev) => [...prev, emptyRow()]);
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      next.add(newIdx);
      return next;
    });
    setEditingIdx(newIdx);
  };

  const handleSubmit = async (channel: OfferteChannel, allowMissingPhone = false) => {
    setSubmitting(channel);
    setSubmitError(null);
    setPhoneConfirmChannel(null);
    try {
      const selectedRows = rows.filter((_, idx) => selectedIdx.has(idx));
      const items = selectedRows
        .map(rowToItem)
        .filter((item): item is CreateOfferteItemInput => item !== null);

      if (items.length === 0) {
        setSubmitError('Selecteer minimaal 1 kleed met geldige afmetingen.');
        return;
      }

      const hasAddress =
        street.trim().length > 0 ||
        postalCode.trim().length > 0 ||
        city.trim().length > 0;

      const kortingRaw = kortingPercentage.trim().replace(',', '.');
      const kortingParsed = kortingRaw ? Number.parseFloat(kortingRaw) : NaN;
      const korting =
        Number.isFinite(kortingParsed) && kortingParsed > 0 ? kortingParsed : null;

      const res = await createOfferte({
        customer: {
          firstName: prefill.customer.firstName || null,
          lastName: prefill.customer.lastName,
          email: prefill.customer.email || null,
          phone: prefill.customer.phone || null,
          company: prefill.customer.company,
          existingAccountId: prefill.customer.accountId,
          existingContactId: prefill.customer.contactId,
        },
        address: hasAddress
          ? {
              street: street || null,
              postalCode: postalCode || null,
              city: city || null,
              houseNumberSuffix: suffix || null,
            }
          : null,
        brand: prefill.brand,
        marketingChannel: 'PHONE',
        packageSku: anySelectedSupportsPackages && packageSku ? packageSku : null,
        items,
        bron: 'Office-App',
        sourceWerkOrderId: prefill.sourceWerkOrderId,
        channel,
        kortingPercentage: korting,
        allowMissingPhone: allowMissingPhone || undefined,
      });

      if (!res.success || !res.leadId) {
        // Ontbrekend telefoonnummer bij een opdracht: bied een bewuste doorzet-optie
        // aan i.p.v. een harde fout — de medewerker kent de klant.
        if (res.errorCode === 'MISSING_PHONE_ORDER') {
          setPhoneConfirmChannel(channel);
          return;
        }
        setSubmitError(res.errorMessage || res.errorCode || 'Aanmaken mislukt');
        return;
      }
      onCreated(res.leadId, channel, res.workOrderId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Vervolgorder voor {customerName}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Op basis van WO {prefill.sourceWerkOrderName}
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {/* Klant */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Klant</h3>
            <div className="text-sm text-slate-900">
              {customerName}
              {prefill.customer.company && (
                <span className="text-slate-500"> · {prefill.customer.company}</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {prefill.customer.email || '—'}
              {prefill.customer.phone && <> · <PhoneNumber number={prefill.customer.phone} sourceContext="repeat-order" className="hover:text-indigo-600" /></>}
            </div>
          </section>

          {/* Adres */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Adres</h3>
            {!adresUitgeklapt ? (
              <div>
                <div className="text-sm text-slate-900">{street || <span className="text-slate-400 italic">geen adres</span>}</div>
                <div className="text-xs text-slate-500">
                  {[postalCode, city].filter(Boolean).join(' ') || '—'}
                </div>
                <button
                  type="button"
                  onClick={() => setAdresUitgeklapt(true)}
                  className="text-xs text-indigo-600 hover:underline mt-1"
                >
                  Adres aanpassen
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <label className="col-span-2 block">
                  <span className="text-xs text-slate-600">Straat + huisnummer</span>
                  <input
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Toevoeging</span>
                  <input
                    value={suffix}
                    onChange={(e) => setSuffix(e.target.value)}
                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-600">Postcode</span>
                  <input
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </label>
                <label className="col-span-2 block">
                  <span className="text-xs text-slate-600">Plaats</span>
                  <input
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                  />
                </label>
              </div>
            )}
          </section>

          {/* Kleden */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Kleden ({rows.length})
            </h3>
            {rows.length === 0 ? (
              <p className="text-xs text-slate-500 italic">Nog geen kleden — voeg er één toe.</p>
            ) : (
              <ul className="space-y-1.5">
                {rows.map((row, idx) => {
                  const product = findOffertePrimaryProduct(row.productCode);
                  const isCountBased = product.mode === 'count';
                  const isEditing = editingIdx === idx;
                  return (
                    <li key={idx} className="border border-slate-200 rounded">
                      <label className="flex items-start gap-2 px-3 py-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedIdx.has(idx)}
                          onChange={() => toggleSelected(idx)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-900">{summarizeKleed(row)}</div>
                          {row.bijzonderheden && !isEditing && (
                            <div className="text-xs text-slate-500 mt-0.5 italic">
                              {row.bijzonderheden}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setEditingIdx(isEditing ? null : idx);
                          }}
                          className="text-xs text-indigo-600 hover:underline shrink-0"
                        >
                          {isEditing ? 'Sluiten' : 'Bewerk'}
                        </button>
                      </label>

                      {isEditing && (
                        <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-2">
                          <label className="block">
                            <span className="text-xs text-slate-600">Product</span>
                            <select
                              value={row.productCode}
                              onChange={(e) => updateRow(idx, { productCode: e.target.value })}
                              className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                            >
                              {OFFERTE_PRIMARY_PRODUCTS.map((p) => (
                                <option key={p.sku} value={p.sku}>{p.label}</option>
                              ))}
                            </select>
                          </label>

                          {isCountBased ? (
                            <label className="block">
                              <span className="text-xs text-slate-600">Aantal</span>
                              <input
                                type="number"
                                min="1"
                                value={row.aantal}
                                onChange={(e) => updateRow(idx, { aantal: e.target.value })}
                                className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                              />
                            </label>
                          ) : (
                            <>
                              <label className="block">
                                <span className="text-xs text-slate-600">Vorm</span>
                                <select
                                  value={row.vorm}
                                  onChange={(e) => updateRow(idx, { vorm: e.target.value as KleedVorm })}
                                  className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white"
                                >
                                  {VORM_OPTIONS.map((v) => (
                                    <option key={v.value} value={v.value}>{v.label}</option>
                                  ))}
                                </select>
                              </label>

                              {row.vorm === 'rechthoekig' && (
                                <div className="grid grid-cols-2 gap-2">
                                  <label className="block">
                                    <span className="text-xs text-slate-600">Lengte (cm)</span>
                                    <input
                                      type="number"
                                      value={row.lengte}
                                      onChange={(e) => updateRow(idx, { lengte: e.target.value })}
                                      className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                                    />
                                  </label>
                                  <label className="block">
                                    <span className="text-xs text-slate-600">Breedte (cm)</span>
                                    <input
                                      type="number"
                                      value={row.breedte}
                                      onChange={(e) => updateRow(idx, { breedte: e.target.value })}
                                      className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                                    />
                                  </label>
                                </div>
                              )}
                              {row.vorm === 'rond' && (
                                <label className="block">
                                  <span className="text-xs text-slate-600">Diameter (cm)</span>
                                  <input
                                    type="number"
                                    value={row.diameter}
                                    onChange={(e) => updateRow(idx, { diameter: e.target.value })}
                                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                                  />
                                </label>
                              )}
                              {row.vorm === 'overig' && (
                                <label className="block">
                                  <span className="text-xs text-slate-600">Oppervlakte (m²)</span>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={row.oppervlakte}
                                    onChange={(e) => updateRow(idx, { oppervlakte: e.target.value })}
                                    className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                                  />
                                </label>
                              )}
                            </>
                          )}

                          <label className="block">
                            <span className="text-xs text-slate-600">Bijzonderheden</span>
                            <textarea
                              rows={2}
                              value={row.bijzonderheden}
                              onChange={(e) => updateRow(idx, { bijzonderheden: e.target.value })}
                              className="mt-0.5 w-full border border-slate-300 rounded px-2 py-1 text-sm"
                            />
                          </label>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <button
              type="button"
              onClick={handleAddKleed}
              className="mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
            >
              + Extra kleed toevoegen
            </button>
            <p className="text-xs text-slate-500 mt-2">
              Prijsoverrides en kortingen uit de bron-order worden niet overgenomen.
              De offerte krijgt verse marktprijzen.
            </p>
          </section>

          {/* Pakket — overgenomen uit bron-order, geldt voor alle kleden */}
          {anySelectedSupportsPackages && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                Pakket
              </h3>
              <select
                value={packageSku}
                onChange={(e) => setPackageSku(e.target.value as PackageSku | '')}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">Geen pakket</option>
                {PACKAGE_SKUS.map((sku) => (
                  <option key={sku} value={sku}>
                    {PACKAGE_LABELS[sku]}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Overgenomen uit de bron-order — geldt voor alle kleden. Pas aan indien nodig.
              </p>
            </section>
          )}

          {/* Korting — optioneel, geldt voor de hele offerte/order */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
              Korting
            </h3>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="decimal"
                value={kortingPercentage}
                onChange={(e) => setKortingPercentage(e.target.value)}
                placeholder="0"
                className="w-24 border border-slate-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-500">% op de hele offerte</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Optioneel. Laat leeg voor geen korting. Achteraf aanpasbaar op de offerte-detail page.
            </p>
          </section>

          {phoneConfirmChannel && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-2">
              <p>
                Deze klant heeft geen telefoonnummer. Een opdracht heeft dat normaal nodig
                voor de routeplanning. Toch doorzetten? De opdracht wordt gemarkeerd zodat
                kantoor het nummer later kan aanvullen.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSubmit(phoneConfirmChannel, true)}
                  disabled={submitting !== null}
                  className="text-xs font-medium px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  {submitting ? 'Aanmaken…' : 'Toch doorzetten zonder telefoonnummer'}
                </button>
                <button
                  type="button"
                  onClick={() => setPhoneConfirmChannel(null)}
                  disabled={submitting !== null}
                  className="text-xs text-amber-700 hover:underline"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}

          {submitError && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting !== null}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSubmit('QUOTE')}
              disabled={!canSubmit}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded border',
                canSubmit
                  ? 'border-indigo-600 text-indigo-600 hover:bg-indigo-50'
                  : 'border-slate-200 text-slate-400 cursor-not-allowed'
              )}
            >
              {submitting === 'QUOTE' ? 'Aanmaken…' : 'Stuur offerte'}
            </button>
            <button
              type="button"
              onClick={() => handleSubmit('ORDER')}
              disabled={!canSubmit}
              className={clsx(
                'text-sm font-medium px-3 py-1.5 rounded',
                canSubmit
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              )}
            >
              {submitting === 'ORDER' ? 'Aanmaken…' : 'Maak opdracht'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
